import { useState } from "react";
import { FileText, FileSpreadsheet, Download, LayoutDashboard, Users, CalendarCheck2, Code2, Target, ClipboardList, TrendingUp, Award, FileStack } from "lucide-react";
import { downloadExport } from "../lib/apiClient";
import { useApp, MONTHS } from "../lib/store";
import { PageHeader } from "../components/Shared";
import { useEmployees, EmployeePicker, Panel } from "../components/Filters";
import { toast } from "sonner";

const MODULES = [
  { id: "dashboard", label: "Dashboard Summary", icon: LayoutDashboard, needsEmp: false },
  { id: "performance", label: "Employee Performance", icon: Users, needsEmp: false },
  { id: "content-calendar", label: "Content Calendar Tracker", icon: CalendarCheck2, needsEmp: false },
  { id: "dev-tasks", label: "Development Task Tracker", icon: Code2, needsEmp: false },
  { id: "kra", label: "Individual KRA Report", icon: Target, needsEmp: true },
  { id: "monthly-review", label: "Monthly Review", icon: ClipboardList, needsEmp: true },
  { id: "quarterly", label: "Quarterly Review", icon: TrendingUp, needsEmp: true },
  { id: "yearly", label: "Yearly Review & Increment", icon: Award, needsEmp: true },
];

export default function Reports() {
  const { month, year } = useApp();
  const employees = useEmployees();
  const [empId, setEmpId] = useState("all");
  const [dept, setDept] = useState("all");
  const [m, setM] = useState(month);
  const [y, setY] = useState(year);

  const run = (mod, format, needsEmp) => {
    const params = { month: m, year: y };
    if (needsEmp) params.employee_id = empId === "all" ? employees[0]?.id : empId;
    else if (empId !== "all") params.employee_id = empId;
    if (dept !== "all") params.department = dept;
    downloadExport(mod, format, params);
    toast.success(`${format.toUpperCase()} export started`, { description: MODULES.find((x) => x.id === mod)?.label });
  };

  return (
    <div>
      <PageHeader title="Reports & Exports" subtitle="Generate management-ready PDF & Excel reports · exports respect all filters below" />

      <Panel title="Report Filters" testid="report-filters">
        <div className="flex flex-wrap gap-3 items-end">
          <div><label className="text-xs font-semibold text-muted-c uppercase block mb-1.5">Month</label>
            <select value={m} onChange={(e) => setM(+e.target.value)} className="input-c" style={{ width: 150 }} data-testid="report-month-select">{MONTHS.map((x) => <option key={x.v} value={x.v}>{x.n}</option>)}</select></div>
          <div><label className="text-xs font-semibold text-muted-c uppercase block mb-1.5">Year</label>
            <select value={y} onChange={(e) => setY(+e.target.value)} className="input-c" style={{ width: 110 }} data-testid="report-year-select">{[2025, 2026, 2027].map((yy) => <option key={yy} value={yy}>{yy}</option>)}</select></div>
          <div><label className="text-xs font-semibold text-muted-c uppercase block mb-1.5">Employee</label>
            <EmployeePicker value={empId} onChange={setEmpId} employees={employees} includeAll testid="report-employee-picker" /></div>
          <div><label className="text-xs font-semibold text-muted-c uppercase block mb-1.5">Department</label>
            <select value={dept} onChange={(e) => setDept(e.target.value)} className="input-c" style={{ width: 160 }} data-testid="report-dept-select"><option value="all">All Departments</option>{["Media", "Engineering", "Marketing", "Design", "Operations"].map((d) => <option key={d}>{d}</option>)}</select></div>
        </div>
      </Panel>

      <div className="card-panel p-6 my-6" data-testid="consolidated-report" style={{ background: "linear-gradient(135deg, rgba(37,99,235,0.12), transparent)" }}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl brand-bg flex items-center justify-center"><FileStack size={24} className="text-white" /></div>
            <div><h3 className="font-display font-bold text-lg">Consolidated Monthly Report</h3><p className="text-sm text-secondary">{MONTHS.find((x) => x.v === m)?.n} {y} · 5 sheets: Summary, Performance, Content Calendar, Dev Tasks, KRA Reviews</p></div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => run("monthly-consolidated", "pdf", false)} className="pill px-4 py-2.5 text-sm font-semibold brand-bg text-white" data-testid="consolidated-pdf-button"><FileText size={16} /> Full PDF</button>
            <button onClick={() => run("monthly-consolidated", "excel", false)} className="pill px-4 py-2.5 text-sm font-semibold surface border-strong hover:brand-text transition-colors" data-testid="consolidated-excel-button"><FileSpreadsheet size={16} /> Full Excel</button>
          </div>
        </div>
      </div>

      <h3 className="font-display font-semibold text-base mb-3">Module-wise Exports</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {MODULES.map((mod) => (
          <div key={mod.id} className="card-panel p-5 flex flex-col gap-4" data-testid={`report-card-${mod.id}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl elevated flex items-center justify-center brand-text"><mod.icon size={19} /></div>
              <div><p className="font-semibold text-sm">{mod.label}</p>{mod.needsEmp && <p className="text-xs text-muted-c">Uses selected employee</p>}</div>
            </div>
            <div className="flex gap-2 mt-auto">
              <button onClick={() => run(mod.id, "pdf", mod.needsEmp)} className="pill px-3 py-2 text-xs font-semibold surface border-strong hover:brand-text transition-colors flex-1 justify-center" data-testid={`${mod.id}-pdf`}><FileText size={14} /> PDF</button>
              <button onClick={() => run(mod.id, "excel", mod.needsEmp)} className="pill px-3 py-2 text-xs font-semibold surface border-strong hover:brand-text transition-colors flex-1 justify-center" data-testid={`${mod.id}-excel`}><FileSpreadsheet size={14} /> Excel</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
